"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetWaterQualityOverTime = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
// Water quality thresholds based on EC (Electrical Conductivity)
// These values are in µS/cm (microsiemens per centimeter)
const WATER_QUALITY_THRESHOLDS = {
    GOOD_MAX: 800,
    MODERATE_MAX: 1500, // 800-1500 µS/cm is moderate
    // Above 1500 µS/cm is poor
};
const ALL_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const getWaterQuality = (ecValue) => {
    if (ecValue <= WATER_QUALITY_THRESHOLDS.GOOD_MAX) {
        return 'good';
    }
    else if (ecValue <= WATER_QUALITY_THRESHOLDS.MODERATE_MAX) {
        return 'moderate';
    }
    else {
        return 'poor';
    }
};
const useGetWaterQualityOverTime = (sensorId) => {
    const [data, setData] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const endDate = (0, react_1.useMemo)(() => {
        // Get current date in Pacific Time using a more reliable method
        const now = new Date();
        const pacificDateString = now.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' });
        const [month, day, year] = pacificDateString.split('/');
        // Format as YYYY-MM-DD
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }, []);
    const startDate = (0, react_1.useMemo)(() => {
        // Get current date in Pacific Time
        const now = new Date();
        // Subtract 7 days from the current date
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 6);
        // Format as YYYY-MM-DD in Pacific Time
        const pacificDateString = sevenDaysAgo.toLocaleDateString('en-US', {
            timeZone: 'America/Los_Angeles',
        });
        const [month, day, year] = pacificDateString.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }, []);
    const lastReported = (0, react_1.useMemo)(() => {
        if (data.length === 0)
            return null;
        const latestDataPoint = data.reduce((latest, current) => current.timestamp > latest.timestamp ? current : latest);
        const date = new Date(latestDataPoint.timestamp);
        return date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    }, [data]);
    const fetchWaterQualityOverTime = async () => {
        if (!sensorId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorUsageRollup,
                variables: {
                    sensorId,
                    startDate,
                    endDate,
                },
            });
            const rawData = result?.data?.getSensorUsageRollup;
            // Handle both direct array and paginated response formats
            const sensorUsageData = Array.isArray(rawData) ? rawData : (rawData?.items || []);
            // Check if sensorUsageData is a valid array
            if (!Array.isArray(sensorUsageData)) {
                setData([]);
                return;
            }
            // Use Pacific Time (PT) for consistency
            const now = new Date();
            const pstDay = new Intl.DateTimeFormat('en-US', {
                timeZone: 'America/Los_Angeles',
                weekday: 'short',
            }).format(now);
            // Find the day index in ALL_DAYS array
            const currentDay = ALL_DAYS.findIndex((day) => day.toLowerCase().includes(pstDay.toLowerCase()));
            // Group data by date and time blocks
            const dataByDateAndTimeBlock = new Map();
            // Filter for water quality data first
            const waterQualityItems = sensorUsageData.filter((item) => item.waterQualityRollup?.maxEC != null);
            // Organize data into blocks by date and time block
            waterQualityItems.forEach((item) => {
                const date = item.date;
                const hour = item.hour ?? 0;
                const timeIndex = Math.floor(hour / 4); // 6 time blocks per day (0-3, 4-7, 8-11, 12-15, 16-19, 20-23)
                if (!dataByDateAndTimeBlock.has(date)) {
                    dataByDateAndTimeBlock.set(date, new Map());
                }
                const dateMap = dataByDateAndTimeBlock.get(date);
                if (!dateMap.has(timeIndex)) {
                    dateMap.set(timeIndex, []);
                }
                dateMap.get(timeIndex).push(item);
            });
            // Process the grouped data to get the max EC value for each time block
            const transformedData = [];
            dataByDateAndTimeBlock.forEach((timeBlocks, dateStr) => {
                const date = new Date(dateStr);
                const dayIndex = (date.getDay() - currentDay + 7) % 7;
                timeBlocks.forEach((blockItems, timeIndex) => {
                    // Find the item with the highest EC value in this time block
                    const maxECItem = blockItems.reduce((max, current) => {
                        const currentEC = current.waterQualityRollup.maxEC;
                        const maxEC = max.waterQualityRollup.maxEC;
                        return currentEC > maxEC ? current : max;
                    });
                    const ecValue = maxECItem.waterQualityRollup.maxEC;
                    const hour = maxECItem.hour ?? 0;
                    const timestamp = maxECItem.createdAt;
                    const dataPoint = {
                        date: dateStr,
                        hour,
                        timestamp,
                        value: ecValue,
                        dayIndex,
                        timeIndex,
                        quality: getWaterQuality(ecValue),
                    };
                    transformedData.push(dataPoint);
                });
            });
            setData(transformedData);
        }
        catch (error) {
            setError(error.message);
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        await fetchWaterQualityOverTime();
    };
    (0, react_1.useEffect)(() => {
        fetchWaterQualityOverTime();
    }, [sensorId]);
    return {
        data,
        loading,
        error,
        refetch,
        lastReported,
    };
};
exports.useGetWaterQualityOverTime = useGetWaterQualityOverTime;
